import { TextInput, useForm } from '@faceup/form'
import { UntitledIcon } from '@faceup/icons'
import { ulMail01 } from '@faceup/icons/ulMail01'
import { ulRefreshCw02 } from '@faceup/icons/ulRefreshCw02'
import { ulTrash01 } from '@faceup/icons/ulTrash01'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { Form } from '@faceup/ui'
import { Alert, Button, Popconfirm, Tag, Typography, useMessage } from '@faceup/ui-base'
import {
  isCompanyEmail as isCompanyEmailFn,
  ResendVictimEmailVerificationResult,
} from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useEffect, useState } from 'react'
import { sharedMessages } from '../Shared/translations'

const messages = defineMessages('followUp.components.emailSubscriptionForm', [
  { id: 'title', defaultMessage: 'Receive updates about your case by email' },
  {
    id: 'label',
    defaultMessage:
      'Get email updates when your case changes. Your email stays private, and notifications never contain conversation content.',
  },
  {
    id: 'subscribedDescription',
    defaultMessage:
      'We are sending notifications about updates in your case to this email. If you no longer want to receive updates, cancel them here:',
  },
  { id: 'unsubscribe', defaultMessage: 'Cancel email updates' },
  { id: 'emailPlaceholder', defaultMessage: 'Your email' },
  {
    id: 'saveCompanyEmail',
    defaultMessage:
      'It appears that you have entered your work email. Are you sure you want to send notifications to this address? We recommend entering your private email to receive notifications.',
  },
  {
    id: 'verificationPendingAlert',
    defaultMessage:
      'We have sent an email to {email}. Please click the link in the email to confirm your address.',
    values: { email: true },
  },
  { id: 'resendVerification', defaultMessage: 'Resend confirmation email' },
  { id: 'changeEmail', defaultMessage: 'Use a different email' },
  { id: 'resendSuccess', defaultMessage: 'Verification email sent' },
  {
    id: 'resendRateLimited',
    defaultMessage: 'Please wait {seconds} seconds before requesting another verification email.',
    values: { seconds: true },
  },
  {
    id: 'resendAlreadyVerified',
    defaultMessage: 'Your email is already verified. Please refresh the page.',
  },
  {
    id: 'resendError',
    defaultMessage:
      "We couldn't resend the verification email. Please refresh the page and try again.",
  },
] as const)

const schema = yup.object().shape({
  email: yup.string().email().required(),
})

export type ResendOutcome = {
  result: ResendVictimEmailVerificationResult
  retryAfterSeconds: number | null
}

type EmailSubscriptionFormProps = {
  maskedEmail: string | null
  isEmailVerified?: boolean
  onSubmit: (email: string) => Promise<boolean>
  onUnsubscribe?: () => Promise<boolean>
  onResend?: () => Promise<ResendOutcome>
}

export const EmailSubscriptionForm = ({
  maskedEmail,
  isEmailVerified,
  onSubmit,
  onUnsubscribe,
  onResend,
}: EmailSubscriptionFormProps) => {
  const [isCompanyEmail, setIsCompanyEmail] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isChangingEmail, setIsChangingEmail] = useState(false)
  const { formatMessage } = useIntl()
  const message = useMessage()

  const form = useForm({
    schema,
    defaultValues: { email: '' },
    afterSubmit: 'persistValues',
  })

  const emailValue = form.watch('email')
  const isValid = form.formState.isValid

  // biome-ignore lint/correctness/useExhaustiveDependencies(form.reset): form.reset changes each render and would cause a loop
  useEffect(() => {
    if (!maskedEmail) {
      form.reset({ email: '' })
    }
  }, [maskedEmail])

  useEffect(() => {
    const checkIsCompanyEmail = async () => setIsCompanyEmail(await isCompanyEmailFn(emailValue))
    void checkIsCompanyEmail()
  }, [emailValue])

  const handleSubmit = async () => {
    const emailToSave = form.getValues('email')
    setIsSubmitting(true)
    try {
      const success = await onSubmit(emailToSave)
      if (success) {
        setIsChangingEmail(false)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUnsubscribe = async () => {
    if (!onUnsubscribe) {
      return
    }
    setIsSubmitting(true)
    try {
      await onUnsubscribe()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (!onResend) {
      return
    }
    setIsResending(true)
    try {
      const { result, retryAfterSeconds } = await onResend()
      switch (result) {
        case ResendVictimEmailVerificationResult.Success:
          message.success(formatMessage(messages.resendSuccess))
          break
        case ResendVictimEmailVerificationResult.RateLimited:
          message.error(
            formatMessage(messages.resendRateLimited, {
              values: { seconds: retryAfterSeconds ?? 0 },
            })
          )
          break
        case ResendVictimEmailVerificationResult.AlreadyVerified:
          message.error(formatMessage(messages.resendAlreadyVerified))
          break
        case ResendVictimEmailVerificationResult.NoEmailSet:
          message.error(formatMessage(messages.resendError))
          break
      }
    } catch {
      message.error(formatMessage(messages.resendError))
    } finally {
      setIsResending(false)
    }
  }

  if (maskedEmail && !isChangingEmail) {
    return (
      <div className='flex flex-col gap-4 w-full'>
        <Typography.Title level={4} className='!m-0'>
          <FormattedMessage message={messages.title} />
        </Typography.Title>
        {isEmailVerified === false ? (
          <>
            <Alert
              type='warning'
              message={
                <FormattedMessage
                  message={messages.verificationPendingAlert}
                  values={{ email: maskedEmail }}
                />
              }
            />
            <div className='flex gap-3'>
              {onResend && (
                <Button
                  type='primary'
                  size='small'
                  icon={<UntitledIcon icon={ulRefreshCw02} />}
                  loading={isResending}
                  onClick={() => handleResend()}
                >
                  <FormattedMessage message={messages.resendVerification} />
                </Button>
              )}
              <Button size='small' onClick={() => setIsChangingEmail(true)}>
                <FormattedMessage message={messages.changeEmail} />
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className='flex items-center gap-2'>
              <Tag color='success' bordered icon={<UntitledIcon icon={ulMail01} size={12} />}>
                {maskedEmail}
              </Tag>
            </div>
            <Typography.Text>
              <FormattedMessage message={messages.subscribedDescription} />
            </Typography.Text>
            {onUnsubscribe && (
              <div>
                <Button
                  type='primary'
                  size='small'
                  icon={<UntitledIcon icon={ulTrash01} />}
                  loading={isSubmitting}
                  onClick={() => handleUnsubscribe()}
                >
                  <FormattedMessage message={messages.unsubscribe} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  const saveButton = isCompanyEmail ? (
    <Popconfirm title={formatMessage(messages.saveCompanyEmail)} onConfirm={() => handleSubmit()}>
      <Button
        type='primary'
        data-test='save-email'
        htmlType='submit'
        loading={isSubmitting}
        disabled={!isValid}
      >
        <FormattedMessage message={sharedMessages.save} />
      </Button>
    </Popconfirm>
  ) : (
    <Button
      type='primary'
      data-test='save-email'
      htmlType='submit'
      loading={isSubmitting}
      disabled={!isValid}
      onClick={() => handleSubmit()}
    >
      <FormattedMessage message={sharedMessages.save} />
    </Button>
  )

  return (
    <div className='flex flex-col gap-4 w-full'>
      <Typography.Title level={4} className='!m-0'>
        <FormattedMessage message={messages.title} />
      </Typography.Title>
      <Form
        onSubmit={() => {
          if (isCompanyEmail) {
            return
          }
          void handleSubmit()
        }}
      >
        <div className='flex flex-col sm:flex-row gap-2 sm:items-end'>
          <div className='flex-1'>
            <TextInput
              withAsterisk={false}
              control={form.control}
              autoComplete='email'
              name='email'
              type='email'
              label={<FormattedMessage message={messages.label} />}
              placeholder={formatMessage(messages.emailPlaceholder)}
            />
          </div>
          {saveButton}
        </div>
      </Form>
    </div>
  )
}
